/**
 * Mobile-only Swiper for card grids (Startseite, /buerger/, /aktuelles/).
 * Activates at ≤ 47em (752 px), matching $break-tablet.
 *
 * Avoids touching the grid element so our theme's display:grid rules never
 * fight Swiper's display:flex. Instead we build a fresh .swiper container,
 * move the card items into individual .swiper-slide wrappers, and hide the
 * original grid. The grid is restored on deactivate / resize up.
 *
 * Two rebuild triggers, because the two filter implementations differ:
 * - watch: "grid"  → highlights-reveal.js / frontpage-news.js replace
 *   grid.innerHTML, so the grid element survives and only its children change.
 * - watch: "shell" → current-news.js replaces the whole results container's
 *   innerHTML, so the grid element itself is thrown away and recreated.
 */

const MOBILE_MQ = "(max-width: 47em)";

/**
 * Slide shadows are extra DOM nodes per slide whose opacity Swiper repaints on
 * every drag frame. On low-end phones that is the most expensive part of the
 * cards effect, so we drop it where it hurts — and where the visitor asked for
 * less motion anyway.
 */
function prefersCheapMotion() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return true;
  }

  const cores = navigator.hardwareConcurrency;
  const memory = navigator.deviceMemory;

  return (
    (typeof cores === "number" && cores > 0 && cores <= 4) ||
    (typeof memory === "number" && memory > 0 && memory <= 4)
  );
}

function createSwiperInstance(swiperEl) {
  return new window.Swiper(swiperEl, {
    effect: "cards",
    grabCursor: true,
    cardsEffect: {
      slideShadows: !prefersCheapMotion(),
    },
  });
}

function buildSwiperWrapper(items) {
  const wrapperEl = document.createElement("div");
  wrapperEl.className = "swiper-wrapper";

  items.forEach((item) => {
    const slide = document.createElement("div");
    slide.className = "swiper-slide";
    slide.appendChild(item); // moves item out of grid
    wrapperEl.appendChild(slide);
  });

  return wrapperEl;
}

/**
 * @param {object} config
 * @param {string} config.sectionId       Section the grid lives in.
 * @param {string} config.shellSelector   Element the swiper is inserted into.
 * @param {string} config.gridSelector    The CSS grid holding the cards.
 * @param {string} config.itemSelector    Direct-child selector for one card.
 * @param {string} config.swiperClass     Extra class on the .swiper element.
 * @param {"grid"|"shell"} [config.watch] Which element the filter replaces.
 */
function initCardSwiper(config) {
  const {
    sectionId,
    shellSelector,
    gridSelector,
    itemSelector,
    swiperClass,
    watch = "grid",
  } = config;

  const section = document.getElementById(sectionId);
  if (!section) return;

  const shell = section.querySelector(shellSelector);
  if (!shell) return;

  let swiperInstance = null;
  let swiperEl = null;
  let grid = null;
  let isMobile = false; // viewport is in swipe range
  let hasSwiper = false; // swiper is currently built
  let isSyncing = false; // our own DOM writes — ignore them in the observer

  const mq = window.matchMedia(MOBILE_MQ);

  const getGridItems = () =>
    grid ? Array.from(grid.querySelectorAll(`:scope > ${itemSelector}`)) : [];

  const destroySwiper = () => {
    if (swiperInstance) {
      swiperInstance.destroy(true, true);
      swiperInstance = null;
    }
  };

  const observeSource = () => {
    const target = watch === "shell" ? shell : grid;
    if (target) observer.observe(target, { childList: true });
  };

  /** Rebuild in place — the grid element survived, only its cards changed. */
  const rebuildInPlace = () => {
    const freshItems = getGridItems();

    if (freshItems.length === 0 || !swiperEl) {
      observeSource();
      return;
    }

    isSyncing = true;
    const oldWrapper = swiperEl.querySelector(".swiper-wrapper");
    const newWrapper = buildSwiperWrapper(freshItems);
    if (oldWrapper) oldWrapper.replaceWith(newWrapper);
    else swiperEl.prepend(newWrapper);
    isSyncing = false;

    swiperInstance = createSwiperInstance(swiperEl);
    observeSource();
  };

  const observer = new MutationObserver(() => {
    if (isSyncing || !isMobile) return;

    observer.disconnect();
    destroySwiper();

    if (watch === "shell") {
      // The whole results container was rewritten: our swiper element and the
      // hidden grid went with it. Start over from the fresh markup.
      swiperEl = null;
      grid = null;
      hasSwiper = false;
      shell.classList.remove("has-card-swiper");
      build();
      return;
    }

    rebuildInPlace();
  });

  function build() {
    if (hasSwiper) return;

    grid = shell.matches(gridSelector)
      ? shell
      : shell.querySelector(gridSelector);

    // No grid (empty filter result) or a single card: nothing to swipe. Keep
    // listening so the swiper returns with the next result set.
    if (!grid || getGridItems().length < 2) {
      observeSource();
      return;
    }

    isSyncing = true;
    swiperEl = document.createElement("div");
    swiperEl.className = `swiper ${swiperClass}`;
    swiperEl.appendChild(buildSwiperWrapper(getGridItems()));

    // Hide original grid (now empty), insert swiper in its place.
    grid.style.display = "none";
    grid.parentNode.insertBefore(swiperEl, grid);
    isSyncing = false;

    hasSwiper = true;
    shell.classList.add("has-card-swiper");

    swiperInstance = createSwiperInstance(swiperEl);
    observeSource();
  }

  function teardown() {
    observer.disconnect();
    destroySwiper();

    if (!hasSwiper) return;

    // Move only our cards back into the grid. Swiper's cards effect appends a
    // .swiper-slide-shadow element to every slide. Moving all child nodes here
    // used to put those shadows into the CSS grid as empty grid items, so after
    // resizing from mobile to desktop every second grid cell was occupied by a
    // shadow and the cards appeared in a zig-zag pattern.
    if (swiperEl && grid) {
      isSyncing = true;
      Array.from(swiperEl.querySelectorAll(".swiper-slide")).forEach((slide) => {
        const item = slide.querySelector(`:scope > ${itemSelector}`);
        if (item) grid.appendChild(item);
      });
      swiperEl.remove();
      isSyncing = false;
    }

    swiperEl = null;
    hasSwiper = false;

    if (grid) grid.style.display = "";
    shell.classList.remove("has-card-swiper");
  }

  mq.addEventListener("change", (event) => {
    isMobile = event.matches;

    if (isMobile) build();
    else teardown();
  });

  if (mq.matches) {
    isMobile = true;
    build();
  }
}

function initHighlightSwiper(sectionId) {
  initCardSwiper({
    sectionId,
    shellSelector: ".highlight-grid-shell",
    gridSelector: ".highlight-grid",
    itemSelector: ".highlight-eq",
    swiperClass: "highlight-swiper",
    watch: "grid",
  });
}

document.addEventListener("DOMContentLoaded", () => {
  if (typeof window.Swiper === "undefined") return;

  initHighlightSwiper("highlights");
  initHighlightSwiper("frontpage-neuigkeiten");

  // /aktuelles/ — same stacked-card swipe. current-news.js swaps the entire
  // results container, so the grid element is not a stable reference here.
  initCardSwiper({
    sectionId: "ws-current-news",
    shellSelector: "[data-current-news-results]",
    gridSelector: "[data-current-news-grid]",
    itemSelector: ".ws-content-card",
    swiperClass: "ws-card-swiper",
    watch: "shell",
  });
});
